import type { ChatMessage } from '../types/chat'
import { userMessageIsPatientSwitchYes } from './patientSwitchConfirm'

type ApiAssistantRow = {
  role?: string
  metadata?: unknown
}

function parseMd(raw: unknown): Record<string, unknown> | undefined {
  if (raw == null) return undefined
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw) as unknown
      return p && typeof p === 'object' ? (p as Record<string, unknown>) : undefined
    } catch {
      return undefined
    }
  }
  return typeof raw === 'object' ? (raw as Record<string, unknown>) : undefined
}

/** API rows include mock open + deferred ECG (reload host before replay). */
export function apiRowsNeedReloadBeforeDeferredEcg(rows: readonly ApiAssistantRow[]): boolean {
  let mockOpen = false
  let pendingMod = false
  for (const r of rows) {
    if (r.role !== 'assistant') continue
    const md = parseMd(r.metadata)
    if (!md) continue
    if (md.mock_patient_page === true || md.mock_patient_page === 'true') mockOpen = true
    if (md.kind === 'pending_modality_exam') {
      const st = md.status === 'done' ? 'done' : 'pending'
      if (st === 'pending') pendingMod = true
    }
  }
  return mockOpen && pendingMod
}

/** Merged chat already has mock panel + pending modality (fallback when API metadata differs). */
export function mergedChatNeedsReloadBeforeDeferredEcg(merged: readonly ChatMessage[]): boolean {
  const mockOpen = merged.some((m) => (m.mockPatientPage?.patientName ?? '').trim().length > 0)
  const pendingMod = merged.some(
    (m) =>
      m.pendingModalityExam?.status === 'pending' || m.pendingModalityExam?.replayPending === true,
  )
  return mockOpen && pendingMod
}

/** API returned open_patient only (deferred ECG runs after host settle, not in this turn). */
export function apiRowsHasOpenPatientWithoutShowModality(rows: readonly ApiAssistantRow[]): boolean {
  let hasOpen = false
  let hasShow = false
  for (const r of rows) {
    if (r.role !== 'assistant') continue
    const md = parseMd(r.metadata)
    if (!md) continue
    if (md.kind === 'open_patient') hasOpen = true
    if (md.kind === 'show_modality') hasShow = true
  }
  return hasOpen && !hasShow
}

/** Backend explicitly armed a deferred document while returning only the open_patient row. */
export function apiRowsHasDeferredOpenEcgOpenPatient(rows: readonly ApiAssistantRow[]): boolean {
  let hasDeferredOpen = false
  let hasShow = false
  for (const r of rows) {
    if (r.role !== 'assistant') continue
    const md = parseMd(r.metadata)
    if (!md) continue
    if (
      md.kind === 'open_patient' &&
      (md.deferred_open_document === true ||
        md.deferred_open_document_pending === true ||
        md.deferred_open_ecg === true ||
        md.deferred_open_ecg_pending === true)
    ) {
      hasDeferredOpen = true
    }
    if (md.kind === 'show_modality') hasShow = true
  }
  return hasDeferredOpen && !hasShow
}

/**
 * Commands that open modality/exam documents need backend metadata (show_modality + file URL),
 * so they must use hosted ``/chat/command`` instead of the text-only host callback lane.
 */
export function utteranceLooksLikeHostedModalityOpen(text: string): boolean {
  const t = (text || '').trim().toLowerCase()
  if (!t) return false
  if (
    !/\b(ecg|electrocardiogram|echo|echocardiogram|ultrasound|scanner|scan|ct|mri|irm|x-?ray|radio(?:graphy)?|doppler|mammography|mammo|blood\s+test|lab\s+test|blood\s+work|bilan\s+sanguin|bilan\s+biologique|prise\s+de\s+sang)\b/.test(
      t,
    )
  ) {
    return false
  }
  return /\b(open|show|view|display|ouvre|ouvrir|affiche|afficher|montre|montrer|voir|latest|recent|previous|dernier|derniere|dernière|precedent|pr[eé]c[eé]dent)\b/.test(
    t,
  )
}

/** Index of the assistant ``open_patient`` row after the latest switch-Yes (cross-patient flow). */
export function findLastCrossPatientOpenAfterSwitchIndex(merged: readonly ChatMessage[]): number {
  let lastYesIdx = -1
  for (let i = merged.length - 1; i >= 0; i--) {
    const m = merged[i]
    if (m.role === 'user' && userMessageIsPatientSwitchYes(m)) {
      lastYesIdx = i
      break
    }
  }
  if (lastYesIdx < 0) return -1
  for (let i = lastYesIdx + 1; i < merged.length; i++) {
    const m = merged[i]
    if (m.role !== 'assistant') continue
    const opened =
      (m.activePatientNameHint ?? '').trim().length > 0 ||
      (m.mockPatientPage?.patientName ?? '').trim().length > 0 ||
      Boolean(m.openPatientDossierUrl)
    if (opened) return i
  }
  return -1
}

/** Replay finished: post-switch ``prescription`` row with PDF URL or explicit fetch error. */
export function crossPatientPrescriptionReplayCompleted(merged: readonly ChatMessage[]): boolean {
  const openIdx = findLastCrossPatientOpenAfterSwitchIndex(merged)
  if (openIdx < 0) return false
  return merged.slice(openIdx + 1).some((m) => {
    if (m.role !== 'assistant' || !m.prescriptionMeta) return false
    if (m.prescriptionMeta.intent_action !== 'view_prescription') return false
    if (m.prescriptionMeta.needs_clarification) return false
    const url = (m.prescriptionDocumentPreviewUrl ?? m.prescriptionMeta.ordonnance_file_url ?? '')
      .trim()
    const err = String(m.prescriptionMeta.ordonnance_fetch_error ?? '').trim()
    return url.length > 0 || err.length > 0
  })
}

/** Replay finished: post-switch exam ``show_modality`` row (ECG / imaging). */
export function crossPatientModalityReplayCompleted(merged: readonly ChatMessage[]): boolean {
  const openIdx = findLastCrossPatientOpenAfterSwitchIndex(merged)
  if (openIdx < 0) return false
  return merged.slice(openIdx + 1).some(
    (m) =>
      m.role === 'assistant' &&
      (Boolean(m.modalityMeta?.filePath) ||
        m.autoOpenModalityFile === true ||
        m.prioritizeDocumentAutoOpen === true),
  )
}

/** Replay finished: post-switch sports NOC guided-flow step (location / sports / summary). */
export function crossPatientSportsNocReplayCompleted(merged: readonly ChatMessage[]): boolean {
  const openIdx = findLastCrossPatientOpenAfterSwitchIndex(merged)
  if (openIdx < 0) return false
  return merged.slice(openIdx + 1).some((m) => {
    if (m.role !== 'assistant' || m.historySnapshot || m.sportsNocFlowCancelled) return false
    return (
      m.sportsNocNeedsClarification === true ||
      m.uiType === 'sports_noc_sports_options' ||
      m.uiType === 'sports_noc_location_options' ||
      m.sportsNocSummary != null ||
      Boolean(m.sportsNocYesNoPrompt)
    )
  })
}

export function crossPatientDeferredDocumentReplayCompleted(merged: readonly ChatMessage[]): boolean {
  return (
    crossPatientPrescriptionReplayCompleted(merged) ||
    crossPatientModalityReplayCompleted(merged) ||
    crossPatientSportsNocReplayCompleted(merged)
  )
}

/**
 * Drop stale prescription auto-open flags from before the post-switch ``open_patient`` row so an
 * old chart's Vidal URL is not opened after switching to another patient.
 */
export function stripStalePrescriptionAutoOpenAfterCrossPatientSwitch(
  merged: readonly ChatMessage[],
): ChatMessage[] {
  const openIdx = findLastCrossPatientOpenAfterSwitchIndex(merged)
  if (openIdx < 0) return [...merged]
  return merged.map((m, i) => {
    if (i > openIdx) return m
    if (!m.autoOpenPrescriptionDocument && !m.prescriptionDocumentPreviewUrl) return m
    return {
      ...m,
      autoOpenPrescriptionDocument: undefined,
      prescriptionDocumentPreviewUrl: undefined,
      prioritizeDocumentAutoOpen: undefined,
      sportsNocCertificatePreview: undefined,
      renewOrdonnanceDocumentPreview: undefined,
    }
  })
}

/** Patient-switch Yes: open target chart, persist chat, reload host, then deferred document replay. */
export function mergedChatCrossPatientOpenBeforeDeferredEcg(merged: readonly ChatMessage[]): boolean {
  const hasResolvedSwitch = merged.some(
    (m) => m.patientSwitchConfirm && m.patientSwitchConfirmResolved === true,
  )
  if (!hasResolvedSwitch) return false
  if (!merged.some((m) => m.role === 'user' && userMessageIsPatientSwitchYes(m))) return false
  const openIdx = findLastCrossPatientOpenAfterSwitchIndex(merged)
  if (openIdx < 0) return false
  if (crossPatientDeferredDocumentReplayCompleted(merged)) return false
  if (
    merged.some(
      (m) =>
        m.pendingModalityExam?.status === 'pending' || m.pendingModalityExam?.replayPending === true,
    )
  ) {
    return false
  }
  return true
}

/** Persist chat + stage replay before host navigation (mock or production dossier). */
export function shouldReloadHostBeforeDeferredCrossPatientEcg(
  rows: readonly ApiAssistantRow[],
  merged: readonly ChatMessage[],
): boolean {
  if (apiRowsNeedReloadBeforeDeferredEcg(rows) || mergedChatNeedsReloadBeforeDeferredEcg(merged)) {
    return true
  }
  if (apiRowsHasDeferredOpenEcgOpenPatient(rows)) return true
  return (
    apiRowsHasOpenPatientWithoutShowModality(rows) &&
    mergedChatCrossPatientOpenBeforeDeferredEcg(merged)
  )
}

export function isPendingModalityReplayStaged(): boolean {
  if (typeof sessionStorage === 'undefined') return false
  try {
    return sessionStorage.getItem('voicnav:pendingModalityAfterReload') === '1'
  } catch {
    return false
  }
}

/**
 * After POST pending-ui-replay, the transcript may still contain an older
 * ``pending_modality_exam`` row while a newer ``show_modality`` row holds the file URL.
 * Mark those placeholders done so defer/auto-open logic does not stay blocked forever.
 */
export function resolvePendingModalityExamRowsAfterDeferredReplay(
  merged: readonly ChatMessage[],
): ChatMessage[] {
  if (!crossPatientDeferredDocumentReplayCompleted(merged)) return [...merged]
  return merged.map((m) => {
    const pm = m.pendingModalityExam
    if (!pm) return m
    if (pm.status !== 'pending' && pm.replayPending !== true) return m
    return {
      ...m,
      pendingModalityExam: {
        ...pm,
        status: 'done' as const,
        replayPending: undefined,
      },
    }
  })
}

/** Block ECG/modality and prescription/blood PDF auto-open until host reload + pending-ui replay. */
export function shouldDeferDocumentAutoOpen(messages: readonly ChatMessage[]): boolean {
  if (crossPatientModalityReplayCompleted(messages) || crossPatientPrescriptionReplayCompleted(messages)) {
    return false
  }
  if (
    messages.some(
      (m) =>
        m.role === 'assistant' &&
        !m.historySnapshot &&
        m.prioritizeDocumentAutoOpen === true &&
        Boolean((m.modalityMeta?.filePath || '').trim()),
    )
  ) {
    return false
  }
  if (isPendingModalityReplayStaged()) return true
  if (mergedChatNeedsReloadBeforeDeferredEcg(messages)) return true
  if (chatHasPendingDeferredModality(messages)) return true
  if (mergedChatCrossPatientOpenBeforeDeferredEcg(messages)) return true
  return false
}

/** Newest assistant prescription row that should open in the preview iframe (post-switch aware). */
export function findLatestPrescriptionDocumentToAutoOpen(
  messages: readonly ChatMessage[],
): { messageId: string; url: string } | null {
  // Backend can mark a current-turn document preview as higher priority than unrelated deferred
  // replay gates from earlier cross-patient flows.
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (
      m.historySnapshot ||
      m.role !== 'assistant' ||
      !m.autoOpenPrescriptionDocument ||
      m.prioritizeDocumentAutoOpen !== true
    ) {
      continue
    }
    const raw = (m.prescriptionDocumentPreviewUrl ?? '').trim()
    if (!raw) continue
    return { messageId: m.id, url: raw }
  }
  if (shouldDeferDocumentAutoOpen(messages)) return null
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.historySnapshot || m.role !== 'assistant' || !m.autoOpenPrescriptionDocument) continue
    const raw = (m.prescriptionDocumentPreviewUrl ?? '').trim()
    if (!raw) continue
    return { messageId: m.id, url: raw }
  }
  return null
}

/** @deprecated Use {@link shouldDeferDocumentAutoOpen} — name kept for call sites that only gate modality. */
export function shouldDeferModalityAutoOpen(messages: readonly ChatMessage[]): boolean {
  return shouldDeferDocumentAutoOpen(messages)
}

function pendingModalityNeedsReplay(pm: ChatMessage['pendingModalityExam']): boolean {
  if (!pm) return false
  if (pm.replayPending === true) return true
  return pm.status === 'pending'
}

/** Chat history still has a deferred ECG row (survives host reload via Redis chat store). */
export function chatHasPendingDeferredModality(messages: readonly ChatMessage[]): boolean {
  return messages.some((m) => pendingModalityNeedsReplay(m.pendingModalityExam))
}
